import React, { useState, useEffect } from 'react';
import { FiFileText } from "react-icons/fi";
import { LuActivity, LuUser } from "react-icons/lu";
import { makeApiCall } from './AssessmentCall';
import './NewAssessment.css';
import { useAlert } from 'react-alert';
import Select from "react-select";
import { IoPrintOutline } from "react-icons/io5";
import { printForm } from './PrintAssessments';
import AssessmentPreview from './NeuroAssessmentPreview';

const GeneralAssessment = ({ data, editData = null, isEditing = false, mode = 'create', onSubmit }) => {
    const currentDate = new Date().toLocaleDateString('en-GB');
    const [loading, setLoading] = useState(false);
    const [showPreview, setShowPreview] = useState({
        show: false,
        showSubmitButton: false,
        onSubmit: () => { }
    });
    const alert = useAlert();

    const isReadOnly = mode === 'view';

    const fieldGroups = {
        patientInfo: {
            title: "Patient Information",
            icon: <LuUser className="section-icon" />,
            fields: [
                { id: 'id', label: 'Patient ID', type: 'text', disabled: true },
                { id: 'name', label: 'Name', type: 'text', disabled: true },
                { id: 'age', label: 'Age', type: 'number', disabled: true },
                {
                    id: 'gender',
                    label: 'Gender',
                    type: 'text',
                    disabled: true
                },
                { id: 'contact_number', label: 'Contact Number', type: 'tel', disabled: true }
            ]
        },
        clinicalHistory: {
            title: "Clinical History",
            icon: <FiFileText className="section-icon" />,
            textareas: [
                { id: 'chief_complaints', label: 'Chief Complaints *', rows: 3 },
                { id: 'present_history', label: 'Present History', rows: 3 },
                { id: 'past_history', label: 'Past History', rows: 3 },
                { id: 'medical_history', label: 'Medical History', rows: 3 },
                { id: 'personal_history', label: 'Personal History', rows: 3 }
            ]
        },
        painAssessment: {
            title: "Pain Assessment",
            icon: <LuActivity className="section-icon" />,
            fields: [
                {
                    id: 'side',
                    label: 'Side',
                    type: 'select',
                    options: [
                        { value: '', label: 'Select side' },
                        { value: 'left', label: 'Left' },
                        { value: 'right', label: 'Right' },
                        { value: 'bilateral', label: 'Bilateral' }
                    ]
                },
                {
                    id: 'type',
                    label: 'Type',
                    type: 'select',
                    options: [
                        { value: '', label: 'Select type' },
                        { value: 'sharp', label: 'Sharp' },
                        { value: 'dull', label: 'Dull' },
                        { value: 'throbbing', label: 'Throbbing' },
                        { value: 'burning', label: 'Burning' },
                        { value: 'aching', label: 'Aching' }
                    ]
                },
            ],
            textareas: [
                { id: 'site', label: 'Site', rows: 2 },
                { id: 'onset', label: 'Onset', rows: 2 },
                { id: 'nature', label: 'Nature', rows: 2 },
                { id: 'duration', label: 'Duration', rows: 2 },
                { id: 'behaviour', label: 'Behaviour', rows: 2 },
                { id: 'aggravating_factor', label: 'Aggravating Factors', rows: 2 },
                { id: 'relieving_factor', label: 'Relieving Factors', rows: 2 }
            ]
        },
        onExam: {
            title: "Diagnosis",
            icon: <FiFileText className="section-icon" />,
            fields: [
                {
                    id: 'built',
                    label: 'Body type',
                    type: 'select',
                    options: [
                        { label: 'Obese (Endomorphic)', value: 'obese' },
                        { label: 'Moderate (Mesomorphic)', value: 'moderate' },
                        { label: 'Thin (Ectomorphic)', value: 'thin' }
                    ],
                },
            ],
            textareas: [
                { id: 'sleep', label: 'Sleeping pattern', rows: 3 },
                { id: 'on_observation', label: 'On observation', rows: 3 },
                { id: 'posture', label: 'Posture assessment', rows: 3 },
                { id: 'external_aids', label: 'External aids', rows: 3 },
                { id: 'on_examination', label: 'On examination', rows: 3 },
            ]
        },
        palpitation: {
            title: "Palpitation",
            icon: <LuActivity className="section-icon" />,
            textareas: [
                { id: 'temperature', label: 'Temperature' },
                { id: 'swelling', label: 'Swelling' },
                { id: 'tenderness', label: 'Tenderness' },
                { id: 'scar', label: 'Scar' },
            ]
        },
        final: {
            title: "Final diagnosis",
            icon: <FiFileText className="section-icon" />,
            textareas: [
                { id: 'sensory', label: 'Sensory examination', rows: 3 },
                { id: 'rom', label: 'Rom', rows: 3 },
                { id: 'specific_test', label: 'Specific tests', rows: 3 },
                { id: 'functional_diagnosis', label: 'Final diagnosis', rows: 3 },
                { id: 'treatment_goal', label: 'Treatment goal', rows: 3 },
            ]
        },
    };

    const flattenApiData = (apiData) => {
        const flattened = {
            therapy_consultation: apiData.therapy_consultation || '',
            id: data?.id || apiData.patient || '',
            name: data?.name || '',
            age: data?.age || '',
            gender: data?.gender || '',
            contact_number: data?.mob || '',
            built: apiData.built || '',
            sleep: apiData.sleep || '',
            posture: apiData.posture || '',
            external_aids: apiData.external_aids || '',
            on_observation: apiData.on_observation || '',
            on_examination: apiData.on_examination || '',
            rom: apiData.rom || '',
            specific_test: apiData.specific_test || '',
            functional_diagnosis: apiData.functional_diagnosis || '',
            treatment_goal: apiData.treatment_goal || '',
            sensory: Array.isArray(apiData.sensory) && apiData.sensory.length > 0 ? apiData.sensory[0].sensory || '' : '',
            chief_complaints: apiData.chief_complaints || '',
        };

        if (apiData.history) {
            flattened.present_history = apiData.history.present_history || '';
            flattened.past_history = apiData.history.past_history || '';
            flattened.medical_history = apiData.history.medical_history || '';
            flattened.personal_history = apiData.history.personal_history || '';
        }

        if (apiData.pain_assessment) {
            flattened.site = apiData.pain_assessment.site || '';
            flattened.side = apiData.pain_assessment.side || '';
            flattened.onset = apiData.pain_assessment.onset || '';
            flattened.duration = apiData.pain_assessment.duration || '';
            flattened.type = apiData.pain_assessment.type || '';
            flattened.nature = apiData.pain_assessment.nature || '';
            flattened.behaviour = apiData.pain_assessment.behaviour || '';
            flattened.aggravating_factor = apiData.pain_assessment.aggravating_factor || '';
            flattened.relieving_factor = apiData.pain_assessment.relieving_factor || '';
        }

        if (apiData.palpitation) {
            flattened.temperature = apiData.palpitation.temperature || '';
            flattened.swelling = apiData.palpitation.swelling || '';
            flattened.tenderness = apiData.palpitation.tenderness || '';
            flattened.scar = apiData.palpitation.scar || '';
        }

        return flattened;
    };

    const getInitialState = () => {
        if ((isEditing || mode === 'view') && editData) {
            return flattenApiData(editData);
        }

        return {
            therapy_consultation: data?.therapy_id || '',
            id: data?.id || '',
            name: data?.name || '',
            age: data?.age || '',
            gender: data?.gender || '',
            contact_number: data?.mob || '',
            built: 'moderate',
            ...Object.fromEntries([
                ...fieldGroups.clinicalHistory.textareas.map(f => [f.id, '']),
                ...fieldGroups.painAssessment.fields.map(f => [f.id, '']),
                ...fieldGroups.painAssessment.textareas.map(f => [f.id, '']),
                ...fieldGroups.palpitation.textareas.map(f => [f.id, '']),
                ...fieldGroups.onExam.textareas.map(f => [f.id, '']),
                ...fieldGroups.final.textareas.map(f => [f.id, '']),
            ])
        };
    };

    const [formData, setFormData] = useState(getInitialState());

    useEffect(() => {
        if ((isEditing || mode === 'view') && editData) {
            setFormData(flattenApiData(editData));
        }
    }, [editData, isEditing, mode]);

    const handleInputChange = (field, value) => {
        if (!isReadOnly) {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleCancel = () => {
        window.scrollTo({
            top: 10,
            behavior: 'smooth'
        });
        if (!isReadOnly) {
            setFormData(getInitialState());
        }
    };

    const handleButtonClick = () => {
        if (!formData.chief_complaints || formData.chief_complaints === "") {
            alert.error("Add chief complaint");
            setLoading(false);
            return;
        }
        setShowPreview({
            show: true,
            showSubmitButton: true,
            onSubmit: handleSubmit,
        });
    };


    const handleSubmit = async () => {
        setShowPreview({
            show: false,
            showSubmitButton: false,
            onSubmit: () => { }
        });
        if (isReadOnly) return;
        setLoading(true);

        const formattedData = {
            patient: +formData.id,
            assessment_form: "general",
        };

        const painAssessmentFields = [
            ...fieldGroups.painAssessment.fields.map(f => f.id),
            ...fieldGroups.painAssessment.textareas.map(f => f.id)
        ];

        const historyFields = [
            'present_history',
            'past_history',
            'medical_history',
            'personal_history'
        ];

        const palpitationFields = fieldGroups.palpitation.textareas.map(f => f.id);

        const excludeFields = ['id', 'name', 'age', 'gender', 'contact_number', 'sensory',
            ...painAssessmentFields, ...historyFields, ...palpitationFields];

        Object.keys(formData).forEach(key => {
            if (!excludeFields.includes(key)) {
                if (isEditing) {
                    formattedData[key] = formData[key] || '';
                } else {
                    if (formData[key] && formData[key] !== '') {
                        formattedData[key] = formData[key];
                    }
                }
            }
        });

        const painAssessment = {};
        let hasPainData = false;

        fieldGroups.painAssessment.fields.forEach(field => {
            if (isEditing) {
                painAssessment[field.id] = formData[field.id] || '';
                if (formData[field.id]) hasPainData = true;
            } else {
                if (formData[field.id] !== undefined && formData[field.id] !== '') {
                    painAssessment[field.id] = formData[field.id];
                    hasPainData = true;
                }
            }
        });

        fieldGroups.painAssessment.textareas.forEach(field => {
            if (isEditing) {
                painAssessment[field.id] = formData[field.id] || '';
                if (formData[field.id]) hasPainData = true;
            } else {
                if (formData[field.id] !== undefined && formData[field.id] !== '') {
                    painAssessment[field.id] = formData[field.id];
                    hasPainData = true;
                }
            }
        });

        const history = {};
        let hasHistoryData = false;

        historyFields.forEach(fieldId => {
            if (isEditing) {
                history[fieldId] = formData[fieldId] || '';
                if (formData[fieldId]) hasHistoryData = true;
            } else {
                if (formData[fieldId] !== undefined && formData[fieldId] !== '') {
                    history[fieldId] = formData[fieldId];
                    hasHistoryData = true;
                }
            }
        });

        const palpitation = {};
        let hasPalpitationData = false;

        palpitationFields.forEach(fieldId => {
            if (isEditing) {
                palpitation[fieldId] = formData[fieldId] || '';
                if (formData[fieldId]) hasPalpitationData = true;
            } else {
                if (formData[fieldId] !== undefined && formData[fieldId] !== '') {
                    palpitation[fieldId] = formData[fieldId];
                    hasPalpitationData = true;
                }
            }
        });

        const sensory_data = [];
        if (isEditing) {
            sensory_data.push({ "sensory": formData.sensory || '' });
        } else {
            if (formData.sensory) {
                sensory_data.push({ "sensory": formData.sensory });
            }
        }

        if (isEditing || Object.keys(painAssessment).length > 0) {
            formattedData.pain_assessment = painAssessment;
        }

        if (isEditing || Object.keys(history).length > 0) {
            formattedData.history = history;
        }

        if (isEditing || Object.keys(palpitation).length > 0) {
            formattedData.palpitation = palpitation;
        }

        if (isEditing || sensory_data.length > 0) {
            formattedData.sensory = sensory_data;
        }

        try {
            let result;
            if (isEditing) {
                result = await makeApiCall({
                    method: 'put',
                    endpoint: `/api/v1/patient/therapy-assessment/?assessment_form=general&assessment_id=${editData.id}`,
                    data: formattedData,
                });
            } else {
                result = await makeApiCall({
                    method: 'post',
                    endpoint: '/api/v1/patient/therapy-assessment/',
                    data: formattedData,
                });
            }

            if (result.success) {
                alert.success(isEditing ? 'Assessment updated successfully!' : 'Assessment saved successfully!');
                if (isEditing) {
                    onSubmit()
                } else {
                    handleCancel();
                }
            } else {
                alert.error(result.error || `Failed to ${isEditing ? 'update' : 'save'} assessment`);
            }
        } catch (error) {
            alert.error(`An error occurred while ${isEditing ? 'updating' : 'saving'} assessment`);
        } finally {
            setLoading(false);
        }
    };

    const handleNullValue = (value, fieldId = null) => {
        if (value === null || value === 'null') {
            return '';
        }
        return value || '';
    };

    const renderInputField = (field) => (
        <div className={`input-group-physio ${field.span ? `span-${field.span}` : ''}`}>
            <label>
                {field.label.replace('*', '')}
                {field.label.includes('*') && <span style={{ color: 'red' }}>*</span>}
            </label>
            {field.type === 'select' ? (
                <Select
                    className="react-select-container no2"
                    classNamePrefix="react-select"
                    styles={{
                        control: (provided) => ({
                            ...provided,
                            minHeight: '40px',
                            lineHeight: '40px',
                            border: '1px solid #E3E3E3',
                        })
                    }}
                    value={field.options.find(opt => opt.value === formData[field.id]) || null}
                    onChange={(selected) => handleInputChange(field.id, selected?.value || '')}
                    options={field.options}
                    placeholder={`Select ${field.label.toLowerCase()}`}
                    isClearable={field.id === 'built' ? false : !!field.options.find(opt => opt.value === '')}
                    isDisabled={field.disabled || isReadOnly}
                />
            ) : (
                <input
                    type={field.type}
                    value={handleNullValue(formData[field.id])}
                    onChange={(e) => handleInputChange(field.id, e.target.value)}
                    placeholder={field.placeholder && field.placeholder}
                    disabled={field.disabled || isReadOnly}
                    readOnly={isReadOnly}
                />
            )}
        </div>
    );

    const renderTextarea = (field) => {
        const maxLength = (field === 'sleep' || field === 'posture' || field === 'external_aids') ? 100 : undefined;
        return (
            <div className="input-group-physio">
                <label>{field.label}</label>
                <textarea
                    value={formData[field.id] || ''}
                    onChange={(e) => handleInputChange(field.id, e.target.value)}
                    placeholder={field.placeholder && field.placeholder}
                    rows={field.rows || 2}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    maxLength={maxLength}
                />
            </div>
        )
    }

    const getFormTitle = () => {
        if (mode === 'view') return 'VIEW GENERAL ASSESSMENT FORM';
        if (isEditing) return 'EDIT GENERAL ASSESSMENT FORM';
        return 'GENERAL ASSESSMENT FORM';
    };

    return (
        <>
            <div className="general-form-content">
                <div className="section-header">
                    <h2>{getFormTitle()}</h2>
                    <IoPrintOutline
                        style={{ marginLeft: 'auto', cursor: 'pointer', fontSize: '20px' }}
                        onClick={() => setShowPreview({
                            show: true,
                            showSubmitButton: false,
                            onSubmit: () => { handleSubmit() }
                        })}
                        title="Print Form"
                    />
                </div>

                <div className="section patient-information">
                    <div className="section-header">
                        {fieldGroups.patientInfo.icon}
                        <h3>{fieldGroups.patientInfo.title}</h3>
                        <label style={{ marginLeft: 'auto' }}>{currentDate}</label>
                    </div>
                    <div className="input-row">
                        {fieldGroups.patientInfo.fields.map(field => renderInputField(field))}
                    </div>
                </div>

                <div className="section clinical-history">
                    <div className="section-header">
                        {fieldGroups.clinicalHistory.icon}
                        <h3>{fieldGroups.clinicalHistory.title}</h3>
                    </div>
                    {fieldGroups.clinicalHistory.textareas.map(field => renderTextarea(field))}
                </div>

                <div className="section pain-assessment">
                    <div className="section-header">
                        {fieldGroups.painAssessment.icon}
                        <h3>{fieldGroups.painAssessment.title}</h3>
                    </div>
                    <div className="input-row">
                        {renderTextarea(fieldGroups.painAssessment.textareas.find(f => f.id === 'site'))}
                        {renderInputField(fieldGroups.painAssessment.fields.find(f => f.id === 'side'))}
                        {renderTextarea(fieldGroups.painAssessment.textareas.find(f => f.id === 'onset'))}
                    </div>
                    <div className="input-row">
                        {renderTextarea(fieldGroups.painAssessment.textareas.find(f => f.id === 'duration'))}
                        {renderInputField(fieldGroups.painAssessment.fields.find(f => f.id === 'type'))}
                        {renderTextarea(fieldGroups.painAssessment.textareas.find(f => f.id === 'nature'))}
                    </div>
                    <div className="input-row">
                        {renderTextarea(fieldGroups.painAssessment.textareas.find(f => f.id === 'behaviour'))}
                        {renderTextarea(fieldGroups.painAssessment.textareas.find(f => f.id === 'aggravating_factor'))}
                        {renderTextarea(fieldGroups.painAssessment.textareas.find(f => f.id === 'relieving_factor'))}
                    </div>
                </div>

                <div className="section clinical-history">
                    {fieldGroups.onExam.fields.map(field => renderInputField(field))}
                    {fieldGroups.onExam.textareas.map(field => renderTextarea(field))}
                </div>

                <div className="section pain-assessment">
                    <div className="section-header">
                        {fieldGroups.palpitation.icon}
                        <h3>{fieldGroups.palpitation.title}</h3>
                    </div>
                    <div className="input-row no2">
                        {fieldGroups.palpitation.textareas.slice(0, 2).map(field => renderTextarea(field))}
                    </div>
                    <div className="input-row no2">
                        {fieldGroups.palpitation.textareas.slice(2, 4).map(field => renderTextarea(field))}
                    </div>
                </div>

                <div className="section clinical-history">
                    {fieldGroups.final.textareas.map(field => renderTextarea(field))}
                </div>
            </div>
            {!isReadOnly && (
                <div className="general-form-actions">
                    <button
                        type="button"
                        disabled={loading}
                        onClick={handleCancel}
                        className="cancel-btn"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        onClick={handleButtonClick}
                        className="submit-btn"
                        style={{ cursor: loading && 'wait' }}
                    >
                        {loading ? (isEditing ? 'Updating...' : 'Saving...') : (isEditing ? 'Update' : 'Save')}
                    </button>
                </div>
            )}
            {showPreview.show && (
                <AssessmentPreview
                    formData={formData}
                    handleSubmit={showPreview.onSubmit}
                    formType='general'
                    onClose={() => setShowPreview({ show: false })}
                    showSubmitButton={showPreview.showSubmitButton}
                />
            )}
        </>
    );
};

export default GeneralAssessment;